import {
    createUniqueId,
    deepEquals,
    exists,
    isNonEmptyString,
} from '@jack-henry/frontend-utils/functions';
import { ObservationSource } from '@jack-henry/frontend-utils/observable';
import type { Action, DeepRequired } from '@treasury/utils';
import { hashString, noOp } from '@treasury/utils/functions';
import { nothing } from 'lit';
import {
    EnrichedTableItem,
    EnrichedTableItemKey,
    TmTableActionInternal,
    TmTableConfigMultiNormalized,
    TmTableConfigNormalized,
    TmTableFilterInternal,
    TmTableOptionsNormalized,
    TmTableRowActionInternal,
    TmTableSelectActionInternal,
    TmTableSelectCustomDefInternal,
    TmTableViewInternal,
    TmTableViewNormalized,
} from './tm-table-internal.types';
import {
    DEFAULT_REDUCER,
    enrichTableItem,
    isEnriched,
    isMultiConfig,
    unwrapEnriched,
} from './tm-table.helpers';
import {
    AllViewId,
    InitialSort,
    NoPagination,
    NullViewId,
    SingleViewId,
    TableClickPayload,
    TmTableColsDef,
    TmTableColumn,
    TmTableFilter,
    TmTableReduceEvent,
    TmTableRowAction,
    TmTableSelectAction,
    TmTableSelectCustomDef,
} from './tm-table.types';

type ViewKey = TmTableViewInternal<never>['viewId'];

const NULL_COLS_DEF = Object.freeze<Required<TmTableColsDef<never>>>({
    canEdit: false,
    definitions: [],
});
const DEFAULT_VIEW = Object.freeze({
    allowMultiSelect: false,
    allowActiveRow: false,
    label: 'All',
    defaultQuery: '',
    noContentMessage: '',
    searchPlaceholder: '',
    subtitle: '',
    columns: { canEdit: false, definitions: [] },
    reduceData: DEFAULT_REDUCER,
    onClick: noOp,
    filters: [],
    selectActions: [],
    tableActions: [],
    displaySettings: noOp,
    refreshData: noOp,
    renderSubheader: () => nothing,
    pagination: NoPagination,
    rowActions: [],
    initialSort: { prop: '' as never, direction: 'asc' as const },
});

const ALL_VIEW = Object.freeze<TmTableViewInternal<unknown>>({
    ...DEFAULT_VIEW,
    viewId: AllViewId,
});

const NULL_VIEW = Object.freeze<TmTableViewInternal<unknown>>({
    ...DEFAULT_VIEW,
    viewId: NullViewId,
});

export interface TableViewState<T> {
    /**
     * The data for the current view or all data if a single view table.
     */
    data: EnrichedTableItem<T>[] | undefined;
    viewId: Exclude<ViewKey, typeof NullViewId>;
    viewLabel?: string;
    page: number;
}

/**
 * Manages translating views available to a `<jha-advanced-table>` from a `<tm-table>`.
 *
 * Provides an API for interacting with a view without needing to know which one is active.
 */
export class TableViewManager<T extends object> {
    public get initialized() {
        return exists(this.optionsHash);
    }

    public get pageNumber() {
        return this._pageNumber;
    }

    private readonly viewStateSource = new ObservationSource<TableViewState<T>>();

    private readonly activeItemSource = new ObservationSource<EnrichedTableItem<T> | undefined>();

    /**
     * Emits the view state every time a view change occurs.
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly viewState$ = this.viewStateSource.toObservable();

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly activeItem$ = this.activeItemSource.toObservable();

    /**
     * A dictionary of IDs to their corresponding table item. Contains all items across views.
     *
     * This is used primarily to look up table items by ID for <jha-advanced-table>
     * without having to force consumers to manage uniquely identifying them manually.
     */
    private readonly itemsMap = new Map<string, EnrichedTableItem<T>>();

    /**
     * Data for each view partitioned into buckets, keyed on view ID.
     */
    private readonly viewDataMap = new Map<ViewKey, EnrichedTableItem<T>[] | undefined>();

    /**
     * A dictionary of table actions for each view,
     * where each view itself is a dictionary keyed on row action ID.
     */
    private readonly rowActionMap = new Map<
        ViewKey,
        Map<string, DeepRequired<TmTableRowActionInternal<T>>>
    >();

    /**
     * Metadata about the views associated with a table, keyed on view ID.
     */
    private readonly viewMap = new Map<ViewKey, TmTableViewInternal<T>>();

    /**
     * Represents the data slice shown by the current view.
     */
    private get viewData() {
        if (this.activeViewId === AllViewId) {
            return Array.from(this.viewDataMap.values()).flat().filter(exists);
        }
        return this.viewDataMap.get(this.activeViewId);
    }

    private set viewData(data) {
        if (this.activeViewId === AllViewId) {
            throw new Error('Cannot set data for the "All" view');
        }

        this.viewDataMap.set(this.activeViewId, data);
    }

    private selectedIds: string[] = [];

    private activeViewId: ViewKey = NullViewId;

    private _query?: string;

    private _showAllView = false;

    /**
     * 1-based page number.
     */
    private _pageNumber = 1;

    private pageSize = 1;

    /**
     * The hash of the last initialized configuration object.
     *
     * Used to determine if the configuration has changed between
     * calls to `init()` and avoid unnecessary re-initialization.
     */
    private optionsHash?: number;

    private get activeView() {
        if (!this.activeViewId) {
            throw new Error('Cannot get active view. Did you call init()?');
        }

        if (this.activeViewId === AllViewId) {
            return ALL_VIEW as unknown as TmTableViewInternal<T>;
        }

        if (this.activeViewId === NullViewId) {
            return NULL_VIEW as unknown as TmTableViewInternal<T>;
        }

        const activeView = this.viewMap.get(this.activeViewId);
        if (!activeView) {
            throw new Error(
                `Cannot get active view with ID "${this.activeViewId.toString()}". It is not known or not valid.`
            );
        }

        return activeView;
    }

    public get query() {
        return this._query;
    }

    public get views() {
        // even though we have a dummy view object for single views,
        // <jha-advanced-table> shows a views UI if the array is populated at all
        if (this.isSingleView) {
            return [];
        }

        return Array.from(this.viewMap.values());
    }

    /**
     * Indicates that this is a manager for a conventional table without multiple views
     * (i.e., one not configured to use the `<jha-advanced-table>` views API).
     */
    public get isSingleView() {
        const keys = Array.from(this.viewMap.keys());
        if (keys.length < 1) {
            return true;
        }

        if (this.activeViewId === SingleViewId) {
            return true;
        }

        // we should never get here, but it doesn't hurt to be defensive
        return keys.length === 1 ? keys[0] === SingleViewId : false;
    }

    public get columns(): Required<TmTableColsDef<T>> {
        if (!exists(this.activeViewId) || !this.activeView.columns) {
            return NULL_COLS_DEF as unknown as Required<TmTableColsDef<T>>;
        }

        if (this.activeViewId === AllViewId) {
            return this.getAllColumns();
        }

        return this.activeView.columns;
    }

    /**
     * The initial sort for the active view (or all views if in "All" view).
     */
    public get initialSort(): InitialSort<T> {
        return this.activeView.initialSort;
    }

    public get filters() {
        return this.activeView.filters.map(f => this.enrichFilter(f));
    }

    public get selectActions() {
        if (Array.isArray(this.activeView.selectActions)) {
            return this.activeView.selectActions.map(a => this.enrichAction(a));
        }

        return [];
    }

    public get multiSelectUiDef() {
        const { selectActions } = this.activeView;

        return Array.isArray(selectActions)
            ? undefined
            : this.enrichMultiSelectCustomDef(selectActions);
    }

    public get rowActions() {
        const rowActions = this.rowActionMap.get(this.activeViewId);
        if (!rowActions) {
            return [];
        }

        return Array.from(rowActions.values());
    }

    public get noContentMessage() {
        const { noContentMessage } = this.activeView;

        return typeof noContentMessage === 'function'
            ? noContentMessage(this._query || '')
            : noContentMessage;
    }

    public get searchPlaceholder() {
        return this.activeView.searchPlaceholder;
    }

    public get allowMultiSelect() {
        return this.activeView.allowMultiSelect;
    }

    public get showAllView() {
        return this.viewMap.size <= 1 ? false : this._showAllView;
    }

    public get subtitle() {
        const { subtitle } = this.activeView;
        return isNonEmptyString(subtitle) ? subtitle : undefined;
    }

    public get minimumQueryLength() {
        const { pagination } = this.activeView;
        if (pagination === NoPagination) {
            return 0;
        }

        return pagination.minQueryLength;
    }

    public get tableActions(): TmTableActionInternal<T>[] {
        return this.activeView.tableActions.map(action => ({
            ...action,
            execute: () => action.execute(this.viewData || []),
        }));
    }

    public get selected() {
        return this.selectedIds
            .map(id => this.itemsMap.get(id))
            .filter(exists)
            .map(unwrapEnriched);
    }

    public get paginationEnabled() {
        return this.activeView.pagination !== NoPagination;
    }

    public get pageLimits() {
        const { pagination } = this.activeView;

        if (pagination === NoPagination) {
            return [];
        }

        return pagination.pageLimits;
    }

    public get allowRefresh() {
        const { refreshData } = this.activeView;
        return exists(refreshData) && refreshData !== noOp;
    }

    /**
     * Initialize the view manager with the table configuration.
     *
     * This is separate from hydrating it with data, which can occur
     * later and/or multiple times with `setData()`.
     *
     * @param debounce If `true`, calls to get additional pages or perform searches
     * will be debounced to prevent overwhelming pagination endpoints.
     *
     * Enabled by default.
     */
    public init(options: TmTableOptionsNormalized<T>) {
        const optionsHash = hashOptions(options);
        if (exists(this.optionsHash) && optionsHash === this.optionsHash) {
            return;
        }

        this.optionsHash = optionsHash;
        this.viewMap.clear();

        if (isMultiConfig<T>(options)) {
            this.initMultiView(options as TmTableConfigMultiNormalized<T>);
        } else {
            this.initSingleView(options as TmTableConfigNormalized<T>);
        }

        this.validateTableActions();
        this.setRowActions();
    }

    /**
     * Sets the initial seed data available to all views.
     */
    public setData(data: T[], pageSize = 10) {
        if (pageSize < 1) {
            throw new Error(`Invalid page limit: ${pageSize}. It must be a positive integer.`);
        }

        this.pageSize = pageSize;
        this.itemsMap.clear();
        this.viewDataMap.clear();
        this.reduceDataAll(TmTableReduceEvent.Hydrate, data);

        const { initialActive } = this.activeView;
        if (initialActive) {
            this.setActive(initialActive);
        }
    }

    /**
     * Set the active view by ID (not intended for external consumers) or index.
     */
    public setActiveView(id: string | typeof AllViewId | number) {
        if (this.isSingleView) {
            throw new Error(
                'Cannot change active view for a single view table; there is only one available.'
            );
        }

        if (typeof id === 'number') {
            id = this.getViewIdByIndex(id) as string;
        }

        if (id !== AllViewId && !this.viewMap.has(id)) {
            throw new Error(`Cannot change view to one with ID "${id}". It is not known.`);
        }

        this.setActiveViewInternal(id);
    }

    /**
     * Programmatically switch to the next view. Wraps around if on the final one.
     *
     * Exists primarily as a helper for tests since it is usually
     * the `<jha-advanced-table>` `@view-change` event that drives this behavior.
     */
    public goToNextView() {
        // this works due to insertion order being maintained in JS maps
        const viewIds = Array.from(this.viewMap.keys());
        const index = viewIds.indexOf(this.activeViewId);

        if (index < 0) {
            throw new Error('Could not go to next view. The current view ID was not known.');
        }

        // wrap around if we're at the end
        const nextIndex = index >= viewIds.length - 1 ? 0 : index + 1;
        this.setActiveView(nextIndex);
    }

    public search(query?: string) {
        if (this._query === query) {
            return undefined;
        }

        this._query = query;
        return this.updateForPageOrQuery(TmTableReduceEvent.Search);
    }

    public async refreshData() {
        if (!this.allowRefresh) {
            throw new Error('Table does not allow refreshing.');
        }

        const newData = await this.activeView.refreshData();
        if (!newData) {
            return;
        }

        this.updateData(newData);
    }

    public setSelected(idsOrItems: string[] | T[]) {
        if (idsOrItems.every(id => typeof id === 'string')) {
            this.selectedIds = idsOrItems;
            return;
        }

        // perform a reverse lookup to determine item IDs
        const enriched = Array.from(this.itemsMap.values());
        this.selectedIds = idsOrItems
            .map(item => {
                const found = enriched.find(e => unwrapEnriched(e) === item);
                return found?.[EnrichedTableItemKey.TableId];
            })
            .filter(exists);
    }

    public click(data: TableClickPayload<T>) {
        if (!this.activeView?.onClick) {
            return;
        }

        this.setActive(data.item);
        this.activeView.onClick(data);
    }

    public pageForward() {
        if (!this.paginationEnabled) {
            throw new Error('Cannot page forward. Pagination has been disabled.');
        }

        this._pageNumber++;
        this.updateForPageOrQuery(TmTableReduceEvent.PageChange);
    }

    public pageBackward() {
        if (!this.paginationEnabled) {
            throw new Error('Cannot page backward. Pagination has been disabled.');
        }

        this._pageNumber--;

        if (this._pageNumber < 1) {
            this._pageNumber = 1;
        }

        this.updateForPageOrQuery(TmTableReduceEvent.PageChange);
    }

    public finalize() {
        if (!this.initialized) {
            return;
        }

        this.resetView();
        this.itemsMap.clear();
        this.viewMap.clear();
        this.viewStateSource.complete();
        this.optionsHash = undefined;
    }

    /**
     * Resets the active view to its default state.
     *
     * Not to be confused with `finalize()`.
     */
    public resetView(clearData = false) {
        const { defaultQuery } = this.activeView;
        this._query = isNonEmptyString(defaultQuery) ? defaultQuery : undefined;
        this.selectedIds = [];
        this._pageNumber = 1;
        this.setActive();

        if (clearData) {
            this.updateData([]);
        }
    }

    /**
     * @param item The row item to execute the action on.
     * @param actionId The internal ID of the action to execute.
     * This is the same as the `id` property of the `<jha-advanced-table>` row action.
     * @throws If the action with the given ID is not found.
     */
    public executeRowAction(item: T, actionId: string) {
        const action = this.rowActionMap.get(this.activeViewId)?.get(actionId);
        if (!action) {
            throw new Error(`Row action with ID "${actionId}" not found.`);
        }

        action.execute(item);
    }

    public renderSubheader() {
        return this.activeView.renderSubheader();
    }

    private setActiveViewInternal(id: ViewKey, emitUpdate = true) {
        this.activeViewId = id;
        this.resetView();

        if (emitUpdate) {
            this.emitUpdate();
        }
    }

    /**
     * Set `item` as the active item of the table while unsetting others.
     *
     * If no item is selected, all items are unset.
     */
    private setActive(item?: T | EnrichedTableItem<T>) {
        if (!this.activeView.allowActiveRow) {
            return;
        }

        const enriched = Array.from(this.itemsMap.values());

        if (!item) {
            enriched.forEach(item => (item[EnrichedTableItemKey.IsActive] = false));
            this.activeItemSource.emit(undefined);
            return;
        }

        const activeItem = isEnriched(item)
            ? item
            : enriched.find(enriched => unwrapEnriched(enriched) === item);

        if (!activeItem) {
            throw new Error('Could not set item to active. It was not known to TableViewManager.');
        }

        enriched.forEach(e => {
            e[EnrichedTableItemKey.IsActive] = unwrapEnriched(e) === unwrapEnriched(activeItem);
        });

        this.activeItemSource.emit(activeItem);
    }

    /**
     * Get all unique values in the current view for a given column (property name).
     */
    private getDistinctValues<K extends keyof T>(propName: K) {
        if (!this.viewData) {
            return [];
        }

        const values = this.viewData.map(v => v[propName]).flat();
        return Array.from(new Set(values))
            .filter(exists)
            .sort((a, b) => {
                // use localeCompare() to sort strings containing numbers
                // numerically rather than lexicographically
                return String(a).localeCompare(String(b), undefined, { numeric: true });
            });
    }

    private initSingleView(config: TmTableConfigNormalized<T>) {
        this.viewMap.set(SingleViewId, {
            viewId: SingleViewId,
            onClick: noOp,
            label: '',
            renderSubheader: () => nothing,
            ...config,
        });

        this.setActiveViewInternal(SingleViewId, false);
    }

    private initMultiView(config: TmTableConfigMultiNormalized<T>) {
        const { views, displayAllView } = config;
        const internalViews = views
            // this must be last in the enrich order since we're installing a label getter;
            // the getter will be lost if a new object is created from it via the spread operator
            .map(v => this.enrichTableView(v));

        internalViews.forEach(v => {
            const { viewId } = v;
            if (viewId === AllViewId || viewId === NullViewId) {
                return;
            }

            this.viewMap.set(viewId, v);
        });

        const [firstView] = internalViews;
        if (firstView.viewId === NullViewId) {
            throw new Error('Cannot set the null view');
        }

        // assume the first view is the active one
        this.setActiveViewInternal(firstView.viewId, false);
        this._showAllView = displayAllView || false;
    }

    /**
     * Invokes the `reduceData()` function on all views with new data.
     */
    private reduceDataAll(event: TmTableReduceEvent, newData?: T[]) {
        this.viewMap.forEach(view => {
            const { reduceData, viewId } = view;
            // call the manager's reduceData() only for
            // the current view to get a single state update
            if (viewId === this.activeViewId) {
                this.reduceData(event, newData);
                return;
            }

            const viewData = this.viewDataMap.get(viewId);
            const vdUnwrapped = exists(viewData) ? viewData.map(unwrapEnriched) : undefined;
            const reducedData =
                reduceData(event, newData || [], vdUnwrapped, this._query) || undefined;

            this.viewDataMap.set(viewId, this.enrichData(reducedData));
        });
    }

    /**
     * Invokes the `reduceData()` function for the current view with new data.
     */
    private reduceData(event: TmTableReduceEvent, newData?: T[]) {
        const { reduceData } = this.activeView;
        const viewData = exists(this.viewData) ? this.viewData.map(unwrapEnriched) : undefined;
        const reducedData = reduceData(event, newData || [], viewData, this._query) || undefined;

        this.updateData(reducedData);
    }

    /**
     * Set data on the current view if it has meaningfully changed.
     *
     * Dispatches update on state change and enriches data if isn't already.
     */
    private async updateData(newData?: T[], oldData = this.viewData) {
        const enriched = this.enrichData(newData);

        // only update if data has meaningfully changed
        if (deepEquals(oldData, enriched)) {
            return;
        }

        this.viewData = enriched;
        this.emitUpdate();
    }

    private emitUpdate() {
        if (this.activeViewId === NullViewId) {
            return;
        }

        const { label } = this.activeView;
        const { _pageNumber: page, viewData: data, activeViewId: viewId } = this;
        const viewLabel = isNonEmptyString(label) ? label : undefined;

        this.viewStateSource.emit({
            viewId,
            viewLabel,
            page,
            data,
        });
    }

    /**
     * Adds new data to the view's dataset based
     * on the current view's `getPage()` callback (if any).
     *
     * Called when the query or page changes.
     */
    private updateForPageOrQuery(
        event: TmTableReduceEvent.PageChange | TmTableReduceEvent.Search
    ): Promise<void> | void {
        if (this.activeView.pagination === NoPagination) {
            return;
        }

        const { getPage } = this.activeView.pagination;
        const newDataResult = getPage(this._pageNumber, this.pageSize, this._query);

        if (newDataResult instanceof Promise) {
            // use then() instead of await so the method can
            // return synchronously under certain circumstances
            // eslint-disable-next-line consistent-return
            return newDataResult.then(newData => {
                this.reduceData(event, newData);
            });
        }

        this.reduceData(event, newDataResult ?? undefined);
    }

    /**
     * Add the data to the table's dataset and enrich with internal metadata.
     */
    private enrichData(data?: T[]) {
        if (!data) {
            return data;
        }

        const enriched = data.map(enrichTableItem);
        enriched.forEach(item => this.itemsMap.set(item[EnrichedTableItemKey.TableId], item));

        return enriched;
    }

    /**
     * Enrich a table view definition with additional properties needed for
     * internal bookkeeping and data known to the `TableViewManager`.
     */
    private enrichTableView(view: TmTableViewNormalized<T>) {
        /**
         * Alias for `this` since we can't use an arrow function on `label` getter.
         */
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        const { label } = view;
        const viewId = createUniqueId();
        const internalView: TmTableViewInternal<T> = {
            viewId,
            ...view,
            get label() {
                if (typeof label === 'string') {
                    return label;
                }

                const data = self.viewDataMap.get(viewId);
                return label(data);
            },
        };

        return internalView;
    }

    /**
     * Hydrate the internal row action collection based on the current views.
     */
    private setRowActions() {
        this.viewMap.forEach(view => {
            const { viewId, rowActions } = view;
            let rowActionsForView = this.rowActionMap.get(viewId);

            if (!rowActionsForView) {
                rowActionsForView = new Map();
                this.rowActionMap.set(viewId, rowActionsForView);
            }

            enrichRowActions(rowActions).forEach(action => {
                rowActionsForView.set(action.actionId, action);
            });
        });
    }

    /**
     * Enrich a bulk select action with behavior related to the view manager.
     */
    private enrichAction(
        action: DeepRequired<TmTableSelectAction<T>>
    ): DeepRequired<TmTableSelectActionInternal<T>> {
        const { label, icon, execute: originalExecute } = action;
        return {
            label,
            icon,
            execute: async () => {
                const result = originalExecute(this.selected);
                const newData = result instanceof Promise ? await result : result;

                this.selectedIds = [];

                if (newData) {
                    const enriched = this.enrichData(newData);
                    this.reduceDataAll(TmTableReduceEvent.BulkAction, enriched);
                }
            },
        };
    }

    /**
     * Enrich a multi-select action definition with behavior related to the view manager.
     * Abstracts away selected items management and data updating.
     */
    private enrichMultiSelectCustomDef(
        def: TmTableSelectCustomDef<T>
    ): TmTableSelectCustomDefInternal<T> {
        const { label, render: originalRender } = def;
        const render = (close: Action) => {
            return originalRender(this.selected, (newData?: T[]) => {
                close();
                if (newData) {
                    const enriched = this.enrichData(newData);
                    this.reduceDataAll(TmTableReduceEvent.BulkAction, enriched);
                }
            });
        };

        return {
            label,
            render,
        };
    }

    private enrichFilter(filter: TmTableFilter<T>) {
        const { prop, type } = filter;
        const choices = this.getDistinctValues(prop as keyof T).map(value => String(value));

        return { prop, type, choices } as TmTableFilterInternal<T>;
    }

    private getViewIdByIndex(index: number) {
        // this works due to insertion order being maintained in JS maps
        const views = Array.from(this.viewMap.values());
        const view = views[index];

        if (!view) {
            throw new Error(
                `Could not get view at index ${index}. Provide a number between 0 and ${views.length - 1}.`
            );
        }

        return view.viewId;
    }

    /**
     * Validate the table actions to ensure they're using a valid icon ID.
     */
    private validateTableActions() {
        this.views.forEach(({ label, tableActions, selectActions, rowActions }) => {
            selectActions = Array.isArray(selectActions) ? selectActions : [];

            const viewVerbiage = label.length > 0 ? ` for view ${label}` : '';
            const actions = [...tableActions, ...selectActions, ...rowActions];

            actions.forEach(action => {
                const { icon } = action;
                // the empty string is used as a default value
                if (icon.length === 0) {
                    return;
                }

                // check to see if there's a corresponding JH UI element defined
                if (!verifyJhIcon(icon)) {
                    const errorMessage = `Invalid table action${viewVerbiage}. The icon identifier "${icon}" is not known.`;
                    throw new Error(errorMessage);
                }
            });
        });
    }

    /**
     * Gets the columns definitions for the "All" view.
     */
    private getAllColumns() {
        /**
         * Columns definitions from all views.
         */
        const defs = Array.from(this.viewMap.values())
            .map(view => view.columns.definitions)
            .flat()
            // dedupe with map based on prop name
            .reduce(
                (map, def) => map.set(def.prop.toString(), def),
                new Map<string, TmTableColumn<T>>()
            )
            .values();

        return {
            ...ALL_VIEW.columns,
            definitions: Array.from(defs),
        };
    }
}

/**
 *
 * @returns `true` if the matching `<jh-icon-*>` element exists for an icon name.
 */
function verifyJhIcon(iconName: string) {
    return exists(customElements.get(`jh-icon-${iconName}`));
}

/**
 * Enrich a row action with metadata needed by the view manager.
 */
function enrichRowActions<T>(
    actions: DeepRequired<TmTableRowAction<T>>[]
): DeepRequired<TmTableRowActionInternal<T>>[] {
    return actions.map(action => ({
        actionId: createUniqueId(),
        ...action,
    }));
}

function hashOptions<T>(options: TmTableOptionsNormalized<T>) {
    return hashString(JSON.stringify(options));
}
